
import * as THREE from "../../libs/three.js/build/three.module.js";
import {copyCoordinatesToClipboard, copyTextToClipboard} from "./CopyFeedback.js";
import {showHoverTooltip, hideHoverTooltip} from "./HoverTooltip.js";
import {hoverHold, hoverRelease} from "./labels/hoverState.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "./ScreenSpaceScale.js";
import {LABEL_RENDER_ORDER} from "./DigitizationStyle.js";

import {t} from "./i18n/index.js";
// Shared "copy coordinates" marker used by every coordinate label across the
// digitization module (FeatureVisual's per-vertex/computed-point labels,
// PipeVisual's result label, ...): a small black two-overlapping-squares
// glyph, click-to-copy, hover tooltip. See createCopyIconMarker()'s own
// comment for why it's a billboarded Mesh rather than a THREE.Sprite.
export function createCopyIconMarker(){
	const size = 64;
	const canvas = document.createElement('canvas');
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext('2d');

	ctx.strokeStyle = '#000';
	ctx.lineWidth = 6;
	ctx.strokeRect(size * 0.14, size * 0.14, size * 0.44, size * 0.44);
	ctx.strokeRect(size * 0.34, size * 0.34, size * 0.44, size * 0.44);

	const texture = new THREE.CanvasTexture(canvas);
	// Mesh, not Sprite: Potree's InputHandler does not raycast-pick Sprites
	// reliably - confirmed, a Sprite-based handle never received a real
	// click. Meshes are the same proven-pickable primitive vertex spheres
	// use. Billboarded manually every frame by the caller (copy its
	// quaternion from the camera) to face the camera like a sprite would.
	const material = new THREE.MeshBasicMaterial({map: texture, depthTest: false, depthWrite: false, transparent: true});
	const geometry = new THREE.PlaneGeometry(1, 1);
	const mesh = new THREE.Mesh(geometry, material);
	mesh.visible = false;
	// user-reported: was getting painted over by a Polygon's own
	// semi-transparent fill (no renderOrder here meant it competed with the
	// fill on THREE's default distance sort) - see DigitizationStyle.js's
	// own doc comment on LABEL_RENDER_ORDER for the full mechanism.
	mesh.renderOrder = LABEL_RENDER_ORDER;

	return mesh;
}

// Wires click-to-copy + hover tooltip onto a marker created by
// createCopyIconMarker(). `getPosition()` is called at click time and
// should return the THREE.Vector3 to copy, or null/undefined to no-op
// (e.g. a computed point that isn't currently valid).
export function wireCopyIcon(icon, getPosition){
	const click = () => {
		const position = getPosition();
		if(position) copyCoordinatesToClipboard(position);
	};

	// InputHandler only ever dispatches 'click' to the object it started
	// dragging on mousedown, and it only starts a drag on an object that has
	// its own 'drag' listener (see InputHandler.onMouseDown/onMouseUp) - so a
	// 'click'-only listener would silently never fire. The no-op 'drag'
	// handler exists purely to make this object a valid drag/click target;
	// the marker itself must never actually move.
	icon.addEventListener("drag", () => {});
	icon.addEventListener("click", click);
	// kopiikonet og den label/det mål, det hører til, er én interaktion: mens musen er over ikonet, bevares hovermålet
	icon.addEventListener("mouseover", () => { showHoverTooltip(t("Kopiér koordinater")); hoverHold(); });
	icon.addEventListener("mouseleave", () => { hideHoverTooltip(); hoverRelease(performance.now()); });
}

// Same as wireCopyIcon(), but copies arbitrary plain text instead of a
// coordinate - used where the label shows computed values rather than a
// single point (e.g. PipeVisual's dimension/koter result label: the copy
// icon should hand over what's actually printed, not the pipe's centerline
// station coordinate). `getText()` is called at click time and should
// return the string to copy, or null/undefined to no-op.
export function wireCopyIconText(icon, getText){
	const click = () => {
		const text = getText();
		if(text) copyTextToClipboard(text, t('Rørmåling kopieret'), t('Kunne ikke kopiere rørmåling'));
	};

	icon.addEventListener("drag", () => {});
	icon.addEventListener("click", click);
	icon.addEventListener("mouseover", () => { showHoverTooltip(t("Kopiér rørmåling")); hoverHold(); });
	icon.addEventListener("mouseleave", () => { hideHoverTooltip(); hoverRelease(performance.now()); });
}

// Tucks `icon` into `label`'s own top-right corner (world space, along the
// camera's local right/up axes, so it stays glued to that corner from any
// viewing angle) and keeps it at a constant on-screen size. Must run AFTER
// the label's own scale is updated for this frame - it reads the label's
// current `label.scale.x` together with its unscaled canvas width/height
// (label.sprite.scale.x/y) to find that corner exactly, without needing to
// duplicate or guess at text-size math. Hides `icon` when `label` isn't
// visible.
// genbrugte vektorer: kaldes pr. label pr. frame
const SCRATCH_RIGHT = new THREE.Vector3();
const SCRATCH_UP = new THREE.Vector3();
const SCRATCH_POS = new THREE.Vector3();

export function positionCopyIconAtLabelCorner(icon, label, camera, clientWidth, clientHeight){
	if(!icon || !label || !label.visible){
		if(icon) icon.visible = false;
		return;
	}

	const right = SCRATCH_RIGHT.setFromMatrixColumn(camera.matrixWorld, 0);
	const up = SCRATCH_UP.setFromMatrixColumn(camera.matrixWorld, 1);

	const halfWidthWorld = (label.sprite.scale.x / 2) * label.scale.x;
	const halfHeightWorld = (label.sprite.scale.y / 2) * label.scale.x;
	const insetX = label.scale.x * 0.12;
	const insetY = label.scale.x * 0.14;
	// label.sprite.position er LabelManagers skærm-forskydning (lokalt rum); ikonet følger den forskudte label
	const pos = SCRATCH_POS.copy(label.position)
		.addScaledVector(label.sprite.position, label.scale.x)
		.addScaledVector(right, halfWidthWorld - insetX)
		.addScaledVector(up, halfHeightWorld - insetY);

	icon.position.copy(pos);
	icon.quaternion.copy(camera.quaternion);
	icon.visible = true;
	// altid lige over sin egen label, hvilken tegnerækkefølge LabelManager end har givet den
	icon.renderOrder = label.sprite.renderOrder + 0.5;

	const scale = computeScreenSpaceScale(camera, pos, camera.position, clientWidth, clientHeight, ScreenSpaceSizes.copyIcon);
	icon.scale.set(scale * 1.2, scale * 1.2, 1);
}
